import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUp, Printer } from "lucide-react";

// -----------------------------------------------------------------------------
// ENCORE THEATRE & GALLERY — Season 8 Company Handbook
// Policies & Procedures — long-form editorial page with sticky TOC.
// -----------------------------------------------------------------------------

const SECTIONS = [
  { id: "who-we-are", num: "02", title: "Who We Are" },
  { id: "encore-covenant", num: "03", title: "The Encore Covenant" },
  { id: "work-sacred", num: "04", title: "The Work Is Sacred" },
  { id: "stewardship", num: "05", title: "Stewardship" },
  { id: "brave-safe", num: "06", title: "Brave Art Requires Safe People" },
  { id: "youth-1", num: "07", title: "Youth Stewardship" },
  { id: "youth-2", num: "08", title: "Privacy, Medication & Reporting" },
  { id: "conflict", num: "09", title: "Conflict Without Chaos" },
  { id: "communication", num: "10", title: "Communication & Accountability" },
  { id: "grace", num: "11", title: "Grace & Accountability" },
  { id: "house-rules", num: "12", title: "Practical House Rules" },
  { id: "sustainability", num: "13", title: "Art & Sustainability" },
];

export default function Policies() {
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    const opts = { rootMargin: "-30% 0px -60% 0px", threshold: 0 };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActive(e.target.id);
      });
    }, opts);
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="policies-page" className="pt-28 pb-24 bg-stone-950 text-stone-300 print:bg-white print:text-black">
      {/* Return + print bar */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-12 flex items-center justify-between gap-4 print:hidden">
        <Link
          to="/about"
          data-testid="policies-back"
          className="inline-flex items-center gap-2 font-overline text-[0.7rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] transition-colors"
        >
          <ArrowLeft size={14} /> Back to About
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="policies-print"
          className="inline-flex items-center gap-2 font-overline text-[0.65rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] transition-colors border border-white/15 hover:border-[#DE9E48] rounded-full px-4 py-2"
        >
          <Printer size={13} strokeWidth={1.75} /> Print / Save PDF
        </button>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-20 md:mb-24">
        <p className="font-overline text-[0.7rem] tracking-[0.28em] text-[#DE9E48] mb-5">
          — Season 8 · Company Handbook
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tighter text-[#FDFBF7] mb-8 max-w-4xl">
          Policies &amp; <span className="italic text-[#DE9E48]">Procedures.</span>
        </h1>
        <p className="font-body italic text-stone-300 text-lg md:text-xl max-w-2xl leading-relaxed">
          A working document about how we treat one another, care for our house
          and make brave art without leaving people behind.
        </p>
      </section>

      {/* Body layout — sticky TOC + prose */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Sticky sidebar TOC */}
        <aside className="lg:col-span-3 print:hidden">
          <div className="lg:sticky lg:top-28">
            <p className="font-overline text-[0.62rem] tracking-[0.24em] text-stone-500 mb-5">
              — Contents
            </p>
            <nav data-testid="policies-toc">
              <ul className="space-y-2.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={`policies-toc-${s.id}`}
                      className={`group flex items-baseline gap-3 py-1 font-body text-sm leading-tight transition-colors ${
                        active === s.id
                          ? "text-[#DE9E48]"
                          : "text-stone-400 hover:text-[#FDFBF7]"
                      }`}
                    >
                      <span className="font-overline text-[0.6rem] tracking-[0.2em] text-stone-600 tabular-nums w-6 flex-shrink-0">
                        {s.num}
                      </span>
                      <span className="tracking-tight">{s.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Prose column */}
        <main className="lg:col-span-9 space-y-24 md:space-y-28 leading-[1.75] print:space-y-16">
          <Section id="who-we-are" num="02" title="Who We Are" subtitle="The House Has a Story">
            <Prose>
              Encore Theatre &amp; Gallery is more than a room where performances
              happen. It is a Birmingham cultural home created to uplift powerful
              stories, cultivate local talent, preserve culture, serve community
              and give emerging artists — especially young people — a creative
              path forward.
            </Prose>

            <SubHead>Our Commitments</SubHead>
            <BulletList
              items={[
                { bold: "Transformative art.", body: "We make work that can delight, disturb, heal, question, remember and move people." },
                { bold: "Black cultural centeredness.", body: "We proudly center African American stories and the breadth of the African diaspora while welcoming stories and collaborators beyond it." },
                { bold: "Excellence with humanity.", body: "High standards and humane treatment are not opposites." },
                { bold: "Access and belonging.", body: "People should be able to enter Encore and feel that the room was prepared with human dignity in mind." },
                { bold: "Local cultivation.", body: "We believe Birmingham talent deserves opportunity, rigor, mentorship and a stage." },
                { bold: "Youth stewardship.", body: "When young people are entrusted to us, their safety and development outrank the convenience of a rehearsal or performance." },
                { bold: "Community.", body: "Audiences, artists, volunteers, neighbors, families, partners and supporters are not abstractions. They are the reason a theatre becomes a living institution." },
              ]}
            />
          </Section>

          <Section id="encore-covenant" num="03" title="The Encore Covenant" subtitle="How We Treat One Another">
            <Prose>
              Theatre is intimate by nature. Encore is intimate by architecture.
              In a small house, voices travel, moods travel, conflict travels and
              care travels. There is little physical distance between backstage
              and audience, between one department and another, or between the
              work and the people making it. That makes awareness of one another
              especially important.
            </Prose>

            <SubHead>Kindness + Clarity</SubHead>
            <Prose>
              Kindness without clarity can leave people guessing. Clarity without
              kindness can make people feel diminished. At Encore, we believe
              people deserve both.
            </Prose>
            <Prose>
              Say what needs to be said. Say it to the right person. Say it as
              clearly as you can. And when you can, say it in a way that leaves
              the other person's dignity intact.
            </Prose>
            <Prose>
              This does not mean avoiding difficult conversations. Sometimes the
              kindest thing we can do is tell the truth early, set a boundary
              clearly or address a problem before it grows. We can be firm without
              being cruel, direct without being demeaning, and accountable
              without becoming punitive.
            </Prose>
            <PullQuote>
              Our goal is not simply to be nice. Our goal is to be good to one
              another while doing good work.
            </PullQuote>

            <SubHead>Our Shared Expectations</SubHead>
            <BulletList
              items={[
                "We speak to people with dignity — especially when correcting, disagreeing or giving notes.",
                "We correct without humiliating, and we receive appropriate corrections without retaliation.",
                "We do not use race, color, national origin, sex, pregnancy, religion, disability, age, sexual orientation, gender identity or expression, or other protected status as a basis for harassment, exclusion or degrading treatment.",
                "We do not bully, threaten, intimidate, haze, sexually harass, stalk or deliberately isolate people.",
                "We do not weaponize gossip, private information, group chats, social media, casting influence or access to leadership.",
                "We recognize that meaningful art can ask us to enter challenging emotional territory. Challenge is never a license for abuse, coercion, humiliation or preventable unsafe conduct.",
                "We respect names, identities, bodies, boundaries, belongings, and reasonable privacy.",
                "We make room for learning. A good-faith mistake can be corrected. Repeated disregard after correction becomes a conduct issue.",
                "We do not confuse closeness with entitlement. Friendship, familiarity, seniority, or artistic chemistry does not create permission to touch, tease, disclose, pressure, or intrude.",
                "We remember that visitors and audiences can hear and see more than we think. Backstage behavior is part of the culture of the house.",
              ]}
            />

            <SubHead>The No-Diva Principle</SubHead>
            <Prose>
              Encore can celebrate extraordinary talent without building a culture
              around entitlement. No artist is so gifted that everyone else must
              absorb disrespect to keep them. No volunteer is "just a volunteer."
              No technician is invisible. A title may determine responsibility and
              decision-making authority; it does not determine anyone's worth.
            </Prose>
            <PullQuote>
              Bring your whole gift. Make room for everybody else's, too.
            </PullQuote>
          </Section>

          <Section id="work-sacred" num="04" title="The Work Is Sacred" subtitle="Rehearsal + Performance Standards">
            <Prose>
              A production is a chain of promises. Punctuality, preparation, and
              communication are how we keep those promises to one another.
            </Prose>

            <SubHead>Attendance, Time + Readiness</SubHead>
            <BulletList
              items={[
                "Disclose known conflicts accurately when requested. Once the schedule is accepted, honor it.",
                "Call time is the time you are expected to be present and ready for the next required step — not the time to begin parking, eating, printing pages, or locating costume pieces.",
                "If an unavoidable delay or emergency occurs, contact the Stage Manager or designated supervisor as soon as possible. We understand that life happens; early communication helps the company adjust with care.",
                "Performances are commitments. Missing a performance without authorization, abandoning a production, chronic lateness, or repeated unexcused absence may result in recasting or removal.",
                "Arrive prepared with the materials, clothing, footwear, water, music, and assigned work required for that rehearsal or performance.",
                "Learn material according to the production's deadlines. If you are struggling, ask for help before a missed deadline becomes a crisis.",
              ]}
            />

            <SubHead>In the Room</SubHead>
            <BulletList
              items={[
                "Keep phones silent and use them only when required for the work, during breaks or for urgent needs.",
                "Give focus to the people actively working. In Encore's close quarters, side conversations can easily become part of someone else's rehearsal.",
                "Receive notes with openness. Questions for clarity are always appropriate; when a larger artistic disagreement needs discussion, find an appropriate moment with the Director rather than allowing the rehearsal to stall in debate.",
                "The Director holds final authority over the artistic staging of the production within Encore policy and safety requirements. The Stage Manager holds operational authority during rehearsals and performances as assigned.",
                "Please do not materially alter blocking, choreography, text, stage business, costume, props, fight or intimacy choreography, or technical practices without authorization. Consistency protects both the work and the people performing it.",
                "Closed rehearsals are closed so artists can work with focus and vulnerability. Guests, family and friends require permission.",
                "Quiet in the wings means quiet. In an intimate room, a whisper can become part of the performance.",
              ]}
            />

            <SubHead>During the Run</SubHead>
            <Prose>
              Remain in the building or designated performance area until
              released. Be ready for entrances and cues. Care for costumes and
              props as instructed. Avoid food, smoke, vaping, or beverages that
              may damage a costume while wearing it unless specifically
              authorized. Never handle technical equipment, weapons, rigging,
              electrical systems, microphones, or effects for which you have not
              been trained or assigned.
            </Prose>
          </Section>

          <Section id="stewardship" num="05" title="Stewardship" subtitle="Take Care of the House">
            <Prose>
              Encore's physical home is intimate, heavily used and shared by
              artists, students, patrons, renters, volunteers, and staff. The
              designated Facilities Manager carries primary operational
              responsibility for the care, maintenance and systems of the
              building. The Facilities Manager should not, however, have to
              follow behind the rest of us simply because caring for the building
              is part of their job. Everyday stewardship belongs to all of us.
            </Prose>
            <PullQuote>
              You do not have to repair the sink. You do have to tell somebody
              it is leaking. That is not maintenance. That is hospitality.
            </PullQuote>

            <SubHead>Everyday Stewardship</SubHead>
            <BulletList
              items={[
                "Please clean up after yourself. Dispose of trash, food containers, cups, tape, paper, and personal items before you leave.",
                "Return rehearsal furniture, props, and shared items to the place designated by the production team.",
                "Keep aisles, backstage paths, exits, doorways, dressing areas, and technical access points clear.",
                "Accidents happen. If something spills, breaks, leaks or stops working, please tell us promptly so we can take care of it. We would much rather know about a mistake than discover an unreported hazard later.",
                "Some areas contain equipment, records, supplies, or hazards that require limited access. Enter technical, storage, office, and facilities areas only when your work requires it or you have permission. If you need something, ask.",
                "Please do not repair, paint, drill, mount, rewire, alter, dispose of, borrow, or remove Encore property without authorization.",
                "Food and drink restrictions may vary by room or production. Follow posted and production-specific guidance.",
                "Before you leave, reset the space so the next person walks into something cared for.",
              ]}
            />

            <SubHead>Facilities Authority</SubHead>
            <Prose>
              Encore's Facilities Manager or designated facilities representative
              oversees the care, access and day-to-day protection of the
              building. This includes keys and access, opening and closing
              procedures, alarms, HVAC, cleaning, waste removal, storage,
              repairs, emergency routes and the general safety of the facility.
            </Prose>
            <Prose>
              If something in the building needs to be changed, moved, repaired,
              accessed or handled differently, ask before improvising. Directors
              and production teams should work with Facilities rather than making
              changes to the building on their own. The goal is not to make the
              building difficult to use. The goal is to make sure everybody who
              uses it helps us keep it usable.
            </Prose>

            <SubHead>Small Room, Shared Air</SubHead>
            <Prose>
              Because Encore is an intimate venue, strong fragrances, smoke or
              vape residue, loud backstage conversation, clutter, illness
              exposure, and food odors can affect many people quickly. Please
              exercise consideration and follow any health, ventilation,
              fragrance, food or occupancy guidance issued for a production or
              event.
            </Prose>
            <PullQuote>
              The building is not a disposable container for the art. It is part
              of the art's possibility.
            </PullQuote>
          </Section>

          <Section id="brave-safe" num="06" title="Brave Art Requires Safe People" subtitle="Safety, Boundaries + Professional Conduct">
            <Prose>
              Encore is committed to a working environment where safety, dignity,
              and professional boundaries are taken seriously. Safety is a shared
              practice: notice hazards, follow qualified instruction, communicate
              boundaries, and report problems.
            </Prose>

            <SubHead>Harassment, Discrimination + Bullying</SubHead>
            <Prose>
              Encore prohibits unlawful discrimination and harassment, sexual
              harassment, bullying, threats, retaliation, and deliberately
              degrading, intimidating or hostile conduct. These expectations
              apply in person and in production-related texts, email, social
              media, rehearsal platforms, and group communications.
            </Prose>

            <SubHead>Consent + Physical Storytelling</SubHead>
            <BulletList
              items={[
                "Stage directions do not erase bodily autonomy. When the work requires physical contact, it should be discussed and choreographed where appropriate and repeatable.",
                "Consent to one action is not blanket consent to other actions. If staging changes, the conversation may need to happen again.",
                "Intimate scenes, simulated violence, stage combat and other heightened physical work should use qualified practices and designated choreography appropriate to the production.",
                "Please do not improvise kisses, slaps, grabs, sexualized contact, fight moves, or other heightened physical actions. Surprises are not a substitute for choreography.",
                "Costume fittings and quick changes should be handled with privacy, communication and only the personnel reasonably needed.",
              ]}
            />

            <SubHead>Alcohol, Drugs + Impairment</SubHead>
            <Prose>
              No one may perform safety-sensitive duties while impaired. Illegal
              drug use on Encore premises or during Encore duties is prohibited.
              Alcohol may not be consumed during rehearsal or performance duties
              unless expressly authorized for a specific lawful event and never
              in a manner that compromises safety or performance. Lawfully
              prescribed medication is not itself a conduct violation; if it
              creates a safety limitation relevant to assigned duties, communicate
              the limitation to the appropriate supervisor without being required
              to disclose unnecessary medical details.
            </Prose>

            <SubHead>Immediate Safety Authority</SubHead>
            <Prose>
              Anyone may call attention to an immediate hazard. Stage Management,
              the Director, Facilities leadership, or other authorized Encore
              leadership may pause activity when safety is in question. If
              something feels immediately unsafe, say so. Emergencies are handled
              first as emergencies; artistic hierarchy comes second.
            </Prose>
            <PullQuote>
              No production is worth preventable harm. We can reset a cue,
              restage a moment and rebuild a prop. People are not expendable
              production elements.
            </PullQuote>

            <SubHead>Personal Hygiene + Shared Space</SubHead>
            <Prose>
              Theatre asks people to work unusually close to one another. We
              share dressing areas, costumes, microphones, props, backstage
              spaces, rehearsal rooms and, sometimes, very little personal
              space. Good personal hygiene is therefore part of being
              considerate of your company.
            </Prose>
            <Prose>
              Company members are expected to arrive for rehearsals and
              performances reasonably clean and appropriately groomed for close
              collaborative work. Please attend to regular bathing, clean
              clothing, oral hygiene, deodorant or other appropriate odor
              management, and the laundering of personal rehearsal garments as
              needed. Costumes and shared wardrobe pieces should be cared for
              according to instructions from the costume team.
            </Prose>
            <Prose>
              At the same time, consideration works both ways. Strong perfumes,
              colognes, body sprays and heavily scented products can also be
              difficult in an intimate theatre and may affect people with
              allergies, asthma, migraines or sensory sensitivities. Please use
              them thoughtfully and follow any fragrance guidance established
              for a production.
            </Prose>
            <Prose>
              If a hygiene concern begins affecting costumes, shared spaces,
              scene partners or the working environment, the appropriate
              supervisor may address it privately, respectfully and directly.
              These conversations should never be used to embarrass someone.
              Whenever possible, the goal is to identify the concern, give the
              person a reasonable opportunity to address it, and preserve their
              dignity.
            </Prose>

            <SubHead>Volunteer Dress + Appearance</SubHead>
            <Prose>
              Volunteers are often among the first people our guests meet, and
              while we do not expect everyone to dress alike, we do ask
              volunteers to arrive clean, neat and appropriately dressed for
              their assignment.
            </Prose>
            <Prose>
              For most public-facing events, volunteers should wear all-black
              attire that feels elevated, polished and professional. This does
              not necessarily mean formal business wear or a suit; the goal is
              simply to look intentional, put-together and appropriate for
              welcoming the public into Encore. Clean black slacks, dresses,
              skirts, blouses, polos, button-down shirts, sweaters and similarly
              polished pieces are all appropriate. Unless otherwise communicated
              for a particular event, please avoid graphic T-shirts, distressed
              clothing, athletic wear, shorts, flip-flops and other overly
              casual attire.
            </Prose>
            <PullQuote>
              We are not looking for everybody to look identical. We are looking
              for everybody to look like they came ready to represent the house.
            </PullQuote>
            <Prose>
              Please avoid clothing with offensive language or imagery,
              excessively revealing attire, or anything that may interfere with
              your responsibilities or distract from the guest experience.
              Closed-toe or otherwise appropriate footwear may be required for
              assignments involving equipment, load-in, food service or other
              safety considerations.
            </Prose>
            <Prose>
              Some events may call for something more festive, casual or
              production-specific. When Encore has a particular look in mind,
              we will communicate it in advance. The goal is not uniformity.
              It is to look prepared, approachable and ready to welcome people
              into the house.
            </Prose>

            <SubHead>Weapons + Stage Weapons</SubHead>
            <Prose>
              Encore is committed to maintaining a safe environment for artists,
              staff, students, volunteers and guests. Weapons are not permitted
              on Encore premises or at Encore-sponsored activities except as
              specifically authorized for legitimate theatrical use and in
              accordance with applicable law.
            </Prose>
            <Prose>
              Any firearm, knife, replica weapon, prop weapon or other object
              intended to function as a weapon in a production must be disclosed
              to and approved by Encore leadership before it is brought into the
              building or incorporated into rehearsal or performance. Stage
              weapons must be stored securely, handled only by authorized and
              appropriately trained personnel, and used only according to
              established choreography and safety procedures.
            </Prose>
            <Prose>
              A prop weapon should be treated with respect even when everyone in
              the room knows it is a prop. No horseplay, joking threats,
              unscripted brandishing or casual handling of stage weapons is
              permitted. Performers may not bring personal weapons for use as
              theatrical props without advance approval.
            </Prose>
            <PullQuote>
              When a production requires a weapon, the goal is simple: the
              audience may believe the danger; the people making the theatre
              should never have to be in danger to create it.
            </PullQuote>
          </Section>

          <Section id="youth-1" num="07" title="Youth Stewardship" subtitle="When Young People Are in Our Care">
            <PullQuote>Their safety outranks the show.</PullQuote>
            <Prose>
              Encore's work with young people carries a heightened duty of care.
              Youth policies apply during classes, camps, rehearsals,
              performances, travel and other Encore-sanctioned programming
              involving minors, together with any program-specific rules.
            </Prose>

            <SubHead>Screening + Supervision</SubHead>
            <BulletList
              items={[
                "Adults assigned to positions that work directly with minors must complete Encore-required screening before beginning regular or unsupervised youth-facing duties. Screening may include criminal-history and sex-offender registry checks as appropriate to the role and law.",
                "Adults backstage with youth during tech and performances must satisfy Encore's applicable youth-safety screening requirements.",
                "Use the Three-Person Rule whenever practicable: avoid isolated one-adult / one-minor situations. If one-to-one interaction is temporarily unavoidable, move to an observable, public or otherwise accountable space as soon as practicable.",
                "Adults may not pursue romantic or sexual relationships with minor participants. Staff and teaching artists must maintain professional boundaries with students and youth participants.",
              ]}
            />

            <SubHead>Communication + Social Media</SubHead>
            <Prose>
              Our young artists deserve caring teachers and mentors without
              blurred personal boundaries.
            </Prose>
            <BulletList
              items={[
                "Communicate with minors only as necessary for legitimate Encore duties and through approved channels. Include parents or guardians where program practice requires it.",
                "Staff and adult volunteers should not use personal social-media accounts to create private relationships with minor participants through the Encore relationship. Keep program communication within approved channels and maintain appropriate adult / youth boundaries.",
                "Photos or video of minors may be created only for authorized program, documentation or promotional purposes and subject to Encore's applicable parent or guardian permissions.",
                "Sexualized, obscene, or age-inappropriate conversation, jokes, media, or discussion of drugs or adult content around minors is prohibited. Adults are responsible for being aware of who can hear them.",
              ]}
            />

            <SubHead>Teaching Practice</SubHead>
            <BulletList
              items={[
                "Youth instruction should emphasize positive reinforcement, age-appropriate care and a growth mindset. Correction can be rigorous without being shaming.",
                "When physical contact is genuinely necessary for instruction, explain exactly what contact is proposed and why; ask an open consent question; be prepared to hear \"no\"; and honor the boundary.",
                "Whenever practical, demonstrate touch-based adjustments on another qualified adult rather than using a minor as the demonstration model. Examples: stage combat, choreography, breath or alignment techniques.",
                "Only persons with appropriate knowledge and training should lead specialized physical work carrying elevated risk.",
                "Encore may use the safety word \"BUTTON.\" If a young person says \"Button\" during a physical exercise or contact-based instruction, we stop first and sort out what is needed second. \"Button\" is not misbehavior, disrespect or refusal to participate. It is a safety tool.",
              ]}
            />
          </Section>

          <Section id="youth-2" num="08" title="Privacy, Medication + Mandatory Reporting" subtitle="Continued from Youth Stewardship">
            <SubHead>Restrooms, Dressing + Costume Changes</SubHead>
            <BulletList
              items={[
                "Where Encore provides single-use restrooms, they are used one person at a time.",
                "Youth may use the appropriate available restroom consistent with Encore policy and applicable law; privacy and safety apply to everyone.",
                "During tech and performances, youth performers may be required to wear designated base layers such as tank tops and shorts beneath costumes to support safe, efficient changes.",
                "Costume-change plans should minimize exposure and unnecessary adult presence. No photography or recording is permitted in changing areas.",
              ]}
            />

            <SubHead>Medication</SubHead>
            <Prose>
              Encore educators and volunteers should not independently dispense
              or administer routine medication to students unless Encore has
              adopted a specific written procedure authorizing trained personnel
              to do so. Parents or guardians remain responsible for
              communicating medication needs and following program procedures.
              Emergency medication and medical emergencies must be handled
              according to the participant's approved plan, staff training,
              applicable law, and emergency instructions. When immediate medical
              help is needed, call emergency services.
            </Prose>

            <SubHead>Suspected Child Abuse or Neglect</SubHead>
            <Prose>
              A disclosure or reasonable suspicion of child abuse or neglect is
              not an ordinary internal grievance. The child's immediate safety
              and any reporting duties required by law come first. Do not
              investigate the matter yourself, interrogate the child, confront
              the suspected person, promise secrecy, or delay a required
              external report while waiting for Encore leadership to decide
              whether the concern is "serious enough."
            </Prose>
            <Prose>
              Encore personnel must comply with Alabama reporting law applicable
              to them. Reports may be made to the appropriate child-protection
              or law-enforcement authority as required by law. Encore may also
              require internal notice to designated leadership so protective
              steps can be taken, but an internal report does not replace an
              external report when the law requires one.
            </Prose>
            <Prose>
              Child-protection law and reporting procedures can change. Encore
              should review this section periodically with qualified Alabama
              counsel and keep current reporting contacts and training
              procedures.
            </Prose>

            <SubHead>If a Child Tells You Something</SubHead>
            <BulletList
              items={[
                "Stay calm and listen.",
                "Do not demand details or conduct your own investigation.",
                "Do not promise that you will keep the information secret. You may say that you will share it only with people who need to help keep them safe.",
                "Record the facts accurately as soon as practicable, using the child's own words where relevant.",
                "Protect the child from immediate danger and make required reports promptly.",
                "Share information only with those who need it for safety, reporting or lawful response.",
              ]}
            />
          </Section>

          <Section id="conflict" num="09" title="Conflict Without Chaos" subtitle="Conflict Resolution">
            <PullQuote>Go toward resolution, not an audience.</PullQuote>
            <Prose>
              Creative work creates friction. People disagree about schedules,
              notes, communication, casting, tone, boundaries, and expectations.
              Conflict is not automatically misconduct, and the existence of
              conflict does not mean a company has failed. The measure of our
              culture is what we do next.
            </Prose>

            <SubHead>Begin With Generosity When You Can</SubHead>
            <Prose>
              Not every short response is disrespect. Not every missed text is
              avoidance. Not every note is personal. Not every disagreement is
              hostility. We are human beings working under deadlines and
              carrying lives outside the theatre that others may know nothing
              about. When circumstances allow, begin by asking rather than
              assuming.
            </Prose>
            <Prose>
              At the same time, kindness does not require anyone to minimize
              behavior that is genuinely harmful, unsafe or inappropriate. Give
              grace where grace is possible. Tell the truth where truth is
              necessary.
            </Prose>

            <SubHead>Our Conflict Principles</SubHead>
            <BulletList
              items={[
                "Address issues early when possible. Small misunderstandings can become large stories when left to circulate.",
                "Speak to the person who can help resolve the problem rather than assembling an audience for it.",
                "Talk about what happened rather than deciding who someone is. Describe the behavior, its impact, and what you need going forward. Avoid labels, personal attacks, and rumors.",
                "Listen for understanding before preparing a rebuttal.",
                "No one is required to confront a person directly when there is a power imbalance, safety concern, harassment allegation, or reasonable fear of retaliation.",
                "Creative disagreement is not insubordination by itself. Leadership may make the final artistic or operational decision after hearing a concern.",
                "A complaint made in good faith is not disloyalty to Encore.",
              ]}
            />

            <SubHead>The Resolution Path</SubHead>
            <BulletList
              items={[
                { bold: "Step One — Direct conversation.", body: "For ordinary misunderstandings or interpersonal friction, when appropriate and safe, speak privately, respectfully and specifically with the person involved." },
                { bold: "Step Two — Stage Manager or immediate supervisor.", body: "Bring scheduling concerns, backstage friction, communication breakdowns, and routine rehearsal or operational concerns here." },
                { bold: "Step Three — Director or department lead.", body: "Bring artistic disputes, repeated conduct problems, departmental concerns or issues not resolved at the prior level here." },
                { bold: "Step Four — Artistic Director or executive leadership.", body: "Serious grievances, policy concerns, unresolved matters, retaliation, or concerns involving the Director or department lead belong here." },
              ]}
            />
            <Prose>
              You may bypass the ordinary path. Harassment, discrimination,
              threats, violence, suspected abuse, significant safety concerns,
              or a complaint involving the person who would ordinarily receive
              it may be taken directly to appropriate Encore leadership, the
              Board of Directors when appropriate, or an outside authority when
              applicable.
            </Prose>

            <SubHead>What Resolution May Include</SubHead>
            <Prose>
              Resolution may include clarification, facilitated conversation,
              apology, changed communication practices, boundary-setting,
              schedule or assignment changes, coaching, mediation, a written
              conduct plan, formal corrective action, separation of
              participants, recasting or removal, or referral to outside
              authorities where appropriate. Encore will not force face-to-face
              mediation when it would be unsafe or inappropriate.
            </Prose>
          </Section>

          <Section id="communication" num="10" title="Communication + Accountability" subtitle="Social Media, Photos + Privacy">
            <Prose>
              We love when our artists are excited about their work at Encore,
              and we encourage you to help celebrate and promote the production.
              We simply ask that you use good judgment and remember that not
              everything happening inside the theatre is yours to share
              publicly.
            </Prose>

            <SubHead>Sharing the Production</SubHead>
            <Prose>
              You are welcome to share official Encore posts, approved
              promotional materials, and content the production team has
              cleared for public use. Please do not reveal costumes, scenic
              elements, scripts, rehearsal footage, choreography, surprises, or
              other production material that has not yet been made public
              without permission.
            </Prose>

            <SubHead>Photos + Recordings</SubHead>
            <Prose>
              Ask before photographing or recording another person in a private
              or backstage setting. Never photograph or record anyone while
              dressing or changing clothes. This is especially important in
              Encore's intimate space, where backstage and changing areas may
              be close together and privacy requires everyone's cooperation.
            </Prose>

            <SubHead>Respect People's Privacy</SubHead>
            <Prose>
              Cast and company contact sheets, phone numbers, email addresses,
              youth information, private schedules, access codes, incident
              reports and other internal information are for company use. Please
              do not post or distribute them outside the people who need them.
            </Prose>

            <SubHead>Keep Company Conflicts Off Social Media</SubHead>
            <Prose>
              If you have a concern about a rehearsal, production, another
              company member or Encore itself, use the conflict-resolution
              process in this handbook so the concern can actually be
              addressed. Social media should not become the rehearsal room
              after rehearsal.
            </Prose>
            <Prose>
              This does not mean you are forbidden from speaking about your own
              experiences, reporting misconduct, raising a safety concern,
              exercising a legally protected right or contacting an appropriate
              outside authority. Encore's conflict-resolution process should
              never be used to silence someone who is seeking help or reporting
              wrongdoing.
            </Prose>

            <SubHead>Your Personal Page Is Your Personal Page</SubHead>
            <Prose>
              Encore does not police the personal beliefs of its artists, staff
              or volunteers. You may have your own political, religious,
              cultural and social views, and you may speak about issues that
              matter to you. We simply ask that you not present your personal
              views as the official position of Encore Theatre &amp; Gallery
              unless you have been authorized to speak on Encore's behalf.
            </Prose>
            <Prose>
              When you publicly identify yourself with Encore, remember that
              people may connect your conduct with the organization. We are not
              asking anyone to become less human, less outspoken or less
              themselves. We simply expect the same respect for colleagues,
              privacy and professional boundaries online that we expect inside
              the building.
            </Prose>
            <PullQuote>
              Celebrate the work. Protect the surprise. Respect people's
              privacy. Handle conflict through the proper channels. Speak for
              yourself unless Encore has asked you to speak for Encore.
            </PullQuote>
          </Section>

          <Section id="grace" num="11" title="Grace + Accountability" subtitle="Correction, Consequences + Restoration">
            <PullQuote>
              We would rather help somebody get it right than wait for an
              opportunity to catch them getting it wrong.
            </PullQuote>
            <Prose>
              Encore believes people can learn, apologize, repair harm and grow.
              When appropriate, correction should teach before it punishes. A
              conversation may be enough. A clearer boundary may be enough. An
              apology and changed behavior may be enough.
            </Prose>
            <Prose>
              But grace is not permission for harmful behavior to continue.
              Accountability is also an act of care — care for the person
              affected, care for the company, and sometimes care for the person
              being corrected. Forgiveness does not require restored access, and
              an apology does not automatically restore a role.
            </Prose>

            <SubHead>Possible Corrective Action</SubHead>
            <Prose>
              Depending on severity, history and circumstances, corrective
              action may include coaching, verbal direction, documented
              warning, behavioral expectations, reassignment, removal from a
              rehearsal or event, recasting, termination of a volunteer,
              contract or work relationship, suspension from Encore activities,
              or ineligibility for future participation. Serious misconduct may
              bypass progressive steps.
            </Prose>

            <SubHead>Conduct That May Justify Immediate Removal</SubHead>
            <Prose>
              Examples include violence or credible threats; serious harassment
              or sexual misconduct; retaliation; theft or intentional property
              damage; deliberate safety violations creating substantial risk;
              possession or use of prohibited weapons contrary to policy;
              working while materially impaired in a safety-sensitive context;
              abuse or exploitation of a minor; deliberate interference with a
              child-protection report; or other conduct Encore reasonably
              determines presents a serious threat to people, the production or
              the organization.
            </Prose>

            <SubHead>No Retaliation</SubHead>
            <Prose>
              Retaliation against a person for raising a good-faith safety
              concern, making a good-faith complaint, participating in an
              investigation, setting a lawful boundary or making a legally
              protected report is prohibited. A complaint that cannot ultimately
              be substantiated is not the same thing as a knowingly false
              accusation made in bad faith.
            </Prose>
            <PullQuote>
              How we behave when no audience is watching is part of our
              artistry. Reputation is built in rehearsal rooms, hallways,
              dressing areas, load-ins and hard conversations.
            </PullQuote>
          </Section>

          <Section id="house-rules" num="12" title="Practical House Rules" subtitle="A Few Things That Keep Us Whole">
            <Prose>
              Some house rules are less poetic than others. They matter anyway.
              These practical expectations help a small, busy theatre function
              safely and respectfully.
            </Prose>
            <BulletList
              items={[
                "Keep emergency exits, fire equipment and access paths unobstructed.",
                "Open flame, pyrotechnic effects, haze or smoke effects, weapons, hazardous materials and special effects require advance approval and appropriate safety planning.",
                "Avoid horseplay in technical, elevated, dark or crowded areas, where a playful moment can quickly become a safety hazard.",
                "Only trained and authorized personnel may operate lighting, sound, rigging, power tools, ladders, lifts, electrical systems or other specialized equipment.",
                "Report injuries, near misses, property damage and significant safety incidents promptly to Stage Management or designated leadership. Reporting helps us prevent the next problem; it is not about assigning blame.",
                "Personal belongings remain the owner's responsibility. Please do not use or move another person's belongings without permission.",
                "Production purchases made with Encore funds remain Encore property unless a written agreement states otherwise.",
                "Access codes, keys and building credentials may not be copied or shared without authorization.",
                "Smoking and vaping are not permitted inside the theatre. Follow all designated-area and event-specific rules.",
                "Service animals and reasonable disability accommodations should be handled respectfully and in accordance with applicable law and Encore procedure.",
                "Press inquiries and official statements about Encore should be referred to authorized leadership.",
                "When a host or rental venue has a lawful safety or use rule that is more restrictive than Encore's house practice, follow the host venue's rule while working there.",
              ]}
            />

            <SubHead>Questions, Reports + Contacts</SubHead>
            <Prose>
              Production-specific contact information should be distributed at
              the start of each process. If you are unsure whom to contact,
              begin with the Stage Manager or your immediate production lead
              unless the nature of the concern makes another reporting path
              more appropriate.
            </Prose>
          </Section>

          <Section id="sustainability" num="13" title="Art + Sustainability" subtitle="Compensation, Stipends + Volunteer Service">
            <PullQuote>
              The work has value — even when the budget cannot fully reflect
              it.
            </PullQuote>
            <Prose>
              Encore believes artists should be valued. That includes valuing
              their time, preparation, craft, training, labor and the years it
              often takes to become good at what they do. We also believe in
              telling the truth about the economics of nonprofit theatre.
            </Prose>
            <Prose>
              Encore Theatre &amp; Gallery is a mission-driven nonprofit arts
              institution. Ticket revenue alone does not carry the full cost of
              producing theatre. Sets, royalties, costumes, musicians,
              designers, utilities, insurance, facilities, marketing,
              technology, education programs and the ordinary work of keeping a
              cultural home alive all compete for limited resources.
            </Prose>
            <Prose>
              There will be productions and programs for which the available
              budget permits meaningful compensation, others for which Encore
              can offer only a modest stipend or honorarium, and some community
              or mission-centered opportunities for which no monetary
              compensation is available.
            </Prose>
            <Prose>
              We understand that passion does not pay a light bill, put gas in
              a car or create more hours in somebody's day. We do not want to
              romanticize unpaid labor. Love of art does not make an artist's
              labor valueless, and "exposure" is not a substitute for wages
              when the law requires wages.
            </Prose>
            <Prose>
              When Encore asks people to participate in a volunteer or
              stipend-based opportunity, we should say so plainly and as early
              as practicable — ideally in the audition notice, offer, agreement
              or volunteer description — so that each person can make an
              informed decision before committing.
            </Prose>

            <SubHead>Our Commitments Around Compensation</SubHead>
            <BulletList
              items={[
                "We will be transparent. Whether an opportunity is paid, stipend-based, honorarium-based, volunteer or unpaid, or reimburses approved expenses should be communicated before acceptance whenever practicable.",
                "We will honor what we promise. If Encore offers a stated stipend, honorarium, wage or reimbursement, the amount, conditions and expected payment timing should be documented and fulfilled according to the applicable agreement.",
                "We will not shame anyone for declining an unpaid or low-stipend opportunity. People have rent, families, transportation, jobs and obligations. \"I cannot afford to do this one\" is a valid answer.",
                "We will not treat compensation level as a measure of artistic worth. A small stipend reflects the production's resources, not the size of a person's talent.",
                "We will pursue growth. As resources expand through audiences, donors, grants, sponsorships and partnerships, increasing sustainable artist and worker compensation should remain an institutional priority.",
                "We will classify relationships responsibly. Calling a payment a stipend or honorarium does not by itself determine whether someone is legally an employee, contractor or volunteer.",
                "We will not use volunteer language to erase legal wage obligations. Where applicable law requires compensation, Encore will not treat required work as unpaid service merely by changing its label.",
              ]}
            />

            <SubHead>The Spirit of the Gift</SubHead>
            <Prose>
              Some seasons are built with abundance. Some are built with faith,
              borrowed tables, somebody's sewing machine, a volunteer at the
              door and artists who decide that a particular story is worth
              carrying together. We honor that gift by never pretending it
              costs nothing.
            </Prose>
            <Prose>
              When someone gives Encore their time without full compensation, we
              understand that they are contributing something of real value to
              the life of this institution and to the cultural life of
              Birmingham.
            </Prose>
            <Prose>
              That generosity is part of how nonprofit art survives — but
              generosity must remain generosity. It should be entered freely,
              described honestly and received with gratitude. Encore's
              responsibility is to steward that gift well, to keep building the
              institution, and to work toward a future in which our ability to
              compensate artists grows alongside our artistic ambition.
            </Prose>
            <PullQuote>
              We make art because stories matter. We keep the doors open because
              a city needs places where those stories can live. And when the
              budget is small, we do not make the people small with it.
            </PullQuote>
          </Section>

          {/* Back to top */}
          <div className="pt-8 border-t border-white/10 flex justify-between items-center print:hidden">
            <p className="font-overline text-[0.6rem] tracking-[0.24em] text-stone-500">
              — End of Handbook · Season 8
            </p>
            <a
              href="#top"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              data-testid="policies-back-to-top"
              className="inline-flex items-center gap-2 font-overline text-[0.65rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] transition-colors"
            >
              <ArrowUp size={14} /> Back to top
            </a>
          </div>
        </main>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

function Section({ id, num, title, subtitle, children }) {
  return (
    <section id={id} className="scroll-mt-28">
      <div className="mb-10">
        <p className="font-overline text-[0.62rem] tracking-[0.28em] text-[#DE9E48] mb-3 tabular-nums">
          — Section {num}
        </p>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter text-[#FDFBF7] mb-3">
          {title}
        </h2>
        <p className="font-body italic text-stone-400 text-base md:text-lg">
          {subtitle}
        </p>
        <div className="mt-6 w-16 h-[2px] bg-[#DE9E48]" />
      </div>
      <div className="space-y-6 text-[1.02rem] text-stone-300">{children}</div>
    </section>
  );
}

function SubHead({ children }) {
  return (
    <h3 className="font-display text-xl md:text-2xl text-[#FDFBF7] tracking-tight mt-10 mb-3">
      {children}
    </h3>
  );
}

function Prose({ children }) {
  return <p className="leading-[1.8]">{children}</p>;
}

function PullQuote({ children }) {
  return (
    <blockquote className="border-l-2 border-[#DE9E48] pl-6 py-1 my-4 font-display italic text-xl md:text-2xl leading-snug text-[#FDFBF7]">
      {children}
    </blockquote>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-3 pl-1">
      {items.map((item, i) => {
        const isObj = typeof item === "object" && item !== null;
        const key = isObj ? item.bold + i : item.slice(0, 32) + i;
        return (
          <li key={key} className="flex items-start gap-3 leading-[1.75]">
            <span className="mt-2.5 w-1 h-1 rounded-full bg-[#DE9E48] flex-shrink-0" />
            <span>
              {isObj ? (
                <>
                  <strong className="text-[#FDFBF7] font-medium">{item.bold}</strong>{" "}
                  {item.body}
                </>
              ) : (
                item
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
